import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@pl/domain';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { forgetMe, useMe, useOrders, useTransfers } from '../../lib/queries';
import { ErrorNote } from '../../ui/bits';
import { Icon, type IconName } from '../../ui/Icon';
import { useScanSite } from '../useScanSite';
import { useTodaysCounts } from '../../lib/todaysCounts';
import s from './Home.module.css';

function greeting(now: Date, timeZone: string): string {
  const hour = Number(now.toLocaleString('en-US', { hour: '2-digit', hourCycle: 'h23', timeZone }));
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

const firstName = (name: string) => name.split(/\s+/).filter(Boolean)[0] ?? name;

function Tile({ icon, name, children }: { icon: IconName; name: string; children: ReactNode }) {
  return (
    <>
      <span className={s.tileIcon}>
        <Icon name={icon} size={28} />
      </span>
      {/* The space keeps the accessible name reading "Ship 2 orders", not "Ship2 orders". */}
      <b className={s.tileName}>{name}</b>{' '}
      <span className={s.tileSub}>{children}</span>
    </>
  );
}

/**
 * The first screen of a shift: the jobs worth a whole tile, box lookup one tap away, and the
 * lead-only actions visible but locked for a worker so nobody hunts for a menu that is not there.
 */
export function Home() {
  const me = useMe();
  const { site } = useScanSite();
  // The count is scoped to the site, and the site only exists once `/auth/me` has answered.
  // Without the guard Home fires an unscoped fetch across every site first and throws it away.
  const orders = useOrders({ siteId: site?.id, status: ['open', 'picking'], limit: 200 }, Boolean(site?.id));
  const incoming = useTransfers({ siteId: site?.id, direction: 'incoming', status: ['in_transit'] }, Boolean(site?.id));
  const counts = useTodaysCounts(site);
  const nav = useNavigate();
  const qc = useQueryClient();

  const signOut = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      // The identity kept for booting without signal goes with the session, or the next person to
      // pick up this phone in a dead spot would be signed in as the one who just signed out.
      forgetMe();
      qc.clear();
      nav('/login', { replace: true });
    },
  });

  if (!me.data) return null;
  const role = me.data.role;
  const lead = can(role, 'write_off');
  const openOrders = orders.data?.length ?? 0;
  const onTheWay = incoming.data?.length ?? 0;

  return (
    <div className="scan-body">
      <p className={s.greet}>
        {greeting(new Date(), site?.timezone ?? 'UTC')}, {firstName(me.data.user.name)}.
      </p>

      {/* The four jobs land the moment `/auth/me` does. Three of them never needed the ship count,
          and Ship's own sub-row is 26px tall whether it holds "Orders to pick" or the badge, so the
          count swaps in place and nothing moves under a thumb. */}
      <div className={s.tiles}>
        <Link to="/scan/receive" className={s.tile}>
          <Tile icon="download" name="Receive">
            Boxes into stock
          </Tile>
        </Link>
        <Link to="/scan/ship" className={s.tile}>
          <Tile icon="truck" name="Ship">
            {orders.data === undefined ? 'Orders to pick' : openOrders > 0 ? <span className="badge ink">{openOrders === 1 ? '1 order' : `${openOrders} orders`}</span> : 'Nothing waiting'}
          </Tile>
        </Link>
        <Link to="/scan/transfer" className={s.tile}>
          <Tile icon="transfer" name="Transfer">
            {onTheWay > 0 ? <span className="badge ink">{`${onTheWay} incoming`}</span> : 'Between sites'}
          </Tile>
        </Link>
        <Link to="/scan/count" className={s.tile}>
          <Tile icon="count" name="Count">
            {counts.suggested.data === undefined || counts.counts.data === undefined ? (
              'Today’s counts'
            ) : counts.left > 0 ? (
              <span className="badge ink">{`${counts.left} left today`}</span>
            ) : counts.total > 0 ? (
              'All done today'
            ) : (
              'Blind counts'
            )}
          </Tile>
        </Link>
      </div>

      {orders.error && (
        <div className={s.ordersError}>
          <ErrorNote error={orders.error} />
          <button type="button" className={s.retry} onClick={() => void orders.refetch()}>
            Try again
          </button>
        </div>
      )}

      <Link to="/scan/lookup" className={s.lookupBar}>
        <span className={s.lookupIcon}>
          <Icon name="scan" size={26} />
        </span>
        <span className={s.lookupText}>
          <b>Look up a box</b>
          <small>Scan or type a box code</small>
        </span>
        <Icon name="chev-right" size={20} />
      </Link>

      <div className={`sect ${s.leadHead}`}>
        <Icon name="lock" size={14} />
        Lead only
      </div>
      <div className={s.leadGrid}>
        {lead ? (
          <>
            <Link to="/scan/lookup?action=found" className={s.leadItem}>
              <Icon name="undo" size={18} />
              Found / return
            </Link>
            <Link to="/scan/lookup?action=write-off" className={s.leadItem}>
              <Icon name="trash" size={18} />
              Write off
            </Link>
          </>
        ) : (
          <>
            <button type="button" className={s.leadItem} disabled>
              <Icon name="lock" size={18} />
              Found / return
            </button>
            <button type="button" className={s.leadItem} disabled>
              <Icon name="lock" size={18} />
              Write off
            </button>
          </>
        )}
      </div>

      <div className={s.foot}>
        <ErrorNote error={signOut.error} />
        {/* The person's own things, not the shift's: small, at the foot, off the job tiles. */}
        <nav className={s.footLinks} aria-label="Account">
          <Link to="/scan/help" className={s.signOut}>
            Help
          </Link>
          <Link to="/scan/password" className={s.signOut}>
            Change password
          </Link>
          <Link to="/scan/whatsapp" className={s.signOut}>
            Link WhatsApp
          </Link>
          <button type="button" className={s.signOut} onClick={() => signOut.mutate()} disabled={signOut.isPending}>
            Sign out
          </button>
        </nav>
      </div>
    </div>
  );
}
